import { ReactElement, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import BackIcon from '../../assets/logo/BackIcon';

export interface Item {
  data: any;
  render: (index: number, itemHeight: number) => ReactElement;
}

interface Props {
  items: Item[];
  itemsPerSlide?: number;
  itemHeight?: number;
  itemWidth?: number;
  autoHeight?: boolean;
  ratio?: number;
  gap?: number;
  hideArrows?: boolean;
  arrowsPosition?: 'top' | 'bottom';
  arrowsClassName?: string;
  itemsClassName?: string;
  headerClassName?: string;
  slideJump?: number;
  title?: ReactElement;
  description?: ReactElement;
}

const CustomCarousel = (props: Props): ReactElement => {
  const {
    items,
    itemsPerSlide: _itemsPerSlide,
    itemHeight: _itemHeight,
    itemWidth: _itemWidth,
    autoHeight,
    gap: _gap,
    hideArrows,
    ratio,
    arrowsPosition,
    slideJump: _slideJump,
    title,
    description,
    arrowsClassName,
    itemsClassName,
    headerClassName,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const sliderWidth = containerRef?.current?.getBoundingClientRect()?.width;

  const gap = _gap ?? 12;

  const itemsPerSlide = _itemsPerSlide ?? 3;

  const itemWidthIncludingGap = _itemWidth ? _itemWidth + gap : sliderWidth / itemsPerSlide - gap;

  const itemWidth = _itemWidth ? _itemWidth + gap : itemWidthIncludingGap - gap;

  const itemHeight = _itemHeight ?? itemWidth * (ratio ?? 1.06);

  const slideJump = (_slideJump ?? itemsPerSlide) - 1;

  const showArrows = items?.length > itemsPerSlide && !hideArrows;

  const [mounted, setMounted] = useState<boolean>(false);

  const [canScrollNxt, setCanScrollNxt] = useState<boolean>(false);
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);

  useEffect(() => {
    const el = containerRef.current;

    if (!el || !mounted) {
      return;
    }

    const updateButtons = () => {
      setCanScrollNxt(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
      setCanScrollPrev(el.scrollLeft > 1);
    };

    updateButtons();

    el.addEventListener('scroll', updateButtons);

    return () => el.removeEventListener('scroll', updateButtons);
  }, [mounted]);

  function onNext(): void {
    containerRef.current.scrollLeft += itemWidthIncludingGap * slideJump;
  }

  function onPrevious(): void {
    containerRef.current.scrollLeft -= itemWidthIncludingGap * slideJump;
  }

  useEffect(() => {
    if (containerRef.current) {
      setMounted(true);
    }
  }, [containerRef.current]);

  return (
    <>
      {arrowsPosition === 'top' && (
        <div className={classnames('flex flex-row items-center justify-between', headerClassName)}>
          <div className="flex flex-col justify-between items-start gap-sm">
            {title}
            {description && description}
          </div>
          {arrowsPosition === 'top' && showArrows && (
            <CustomButtonGroup
              next={onNext}
              previous={onPrevious}
              className={arrowsClassName}
              canScrollNxt={canScrollNxt}
              canScrollPrev={canScrollPrev}
            />
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className={classnames('w-full overflow-x-auto scrollbar-hide scroll-smooth snap-x', itemsClassName)}
      >
        {mounted && (
          <div
            className="flex py-xs"
            style={{
              width: itemWidthIncludingGap * items?.length,
            }}
          >
            {items?.map((item, index) => (
              <div
                key={index?.toString()}
                className="snap-star z-20"
                style={{
                  width: itemWidthIncludingGap,
                  height: autoHeight ? undefined : itemHeight,
                  paddingRight: gap,
                }}
              >
                {item.render(index, itemHeight)}
              </div>
            ))}
          </div>
        )}
      </div>
      {arrowsPosition !== 'top' && showArrows && (
        <CustomButtonGroup
          next={onNext}
          previous={onPrevious}
          className={classnames('mt-sm', arrowsClassName)}
          canScrollNxt={canScrollNxt}
          canScrollPrev={canScrollPrev}
        />
      )}
    </>
  );
};

export default CustomCarousel;

const CustomButtonGroup = ({
  next,
  previous,
  className,
  canScrollPrev,
  canScrollNxt,
}: {
  next?: any;
  previous?: any;
  className?: string;
  canScrollPrev?: boolean;
  canScrollNxt?: boolean;
}) => {
  const Arrows = ({ onClick, isRight, canScroll }: { onClick: () => void; isRight?: boolean; canScroll?: boolean }) => {
    return (
      <button
        className={classnames('size-[30px] rounded-[8px] flex justify-center items-center', {
          'bg-[#ebebec]': !canScroll,
          'border hover:bg-[#f6f6f6]': canScroll,
        })}
        onClick={onClick}
      >
        <BackIcon className={classnames('size-3', isRight && 'rotate-180')} color={canScroll ? 'black' : '#4D4D4D'} />
      </button>
    );
  };

  return (
    <div className={classnames('flex justify-end lg:gap-sm gap-xs', className)}>
      <Arrows onClick={previous} canScroll={canScrollPrev} />
      <Arrows onClick={next} isRight canScroll={canScrollNxt} />
    </div>
  );
};
